import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Card,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Badge,
  Progress,
  Spinner,
} from "reactstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import styles from "../css/studyworkflow.module.scss";
import {
  Checklist_Type_OPTIONS,
  loadCodelistsDetailId,
} from "api/modules/common.option";
import SimpleBar from "simplebar-react";
import { GetUserData, showToastMessage } from "common/service/CommonService";
import FullPageLoader from "Components/Common/FullPageLoader";
import CommonForm from "Components/Common/CommonForm";
import { useCheckListMasterService } from "modules/Library/Checklist/api/useChecklist";
import { formatDateTime, formatDateTimeIST } from "helpers/crf.utils";
import ViewStudyDetails from "modules/Study/Study/pages/ViewStudyDetails";
import {
  useQueryWorkflowData,
  useSelectWorkflowData,
  useStageSelectWorkflowData,
} from "modules/Library/Workflow/api/useWorkflowQuery";
import QueryDetailsModal from "../components/QueryDetailsModal";
import { queryClient } from "api/queryClient";
import TruncatedText from "Components/Common/TruncatedText";

const WorkflowReview: React.FC = () => {
  document.title = "Workflow Review | ZENTIXS VISTA";
  
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : null;
  const userData = GetUserData();
  const { control, reset, watch, getValues } = useForm();
  const [isQueryModalOpen, setIsQueryModalOpen] = useState(false);
  const toggleQueryModal = () => setIsQueryModalOpen(!isQueryModalOpen);

  // Fetch Master ICF Questions
  const { SelectAllQuery: MasterICFQuery } = useCheckListMasterService({
    ispagination: false,
    checklisttype: "STUDY",
  });

  // -----------------------------------------
  // WORKFLOW FETCHING & DYNAMIC PROGRESS BAR
  // Fetching Workflow ID dynamically from route state fallback to 5
  const routeWorkflowId = location.state?.workflowid || 5;
  const { selectQuery: workflowQuery } = useSelectWorkflowData(routeWorkflowId);
  const { stageSelectQuery, switchStageMutation } = useStageSelectWorkflowData({
    studyid: numericId,
    workflowid: routeWorkflowId,
  });

  const queryPayload = useMemo(
    () => ({
      studyid: numericId,
      pagenumber: 1,
      pagesize: 10,
    }),
    [numericId],
  );

  const { querySelectAll } = useQueryWorkflowData(queryPayload);

  const currentUserRoleId = Number(userData?.roleid);

  const hasMyActiveQueries = useMemo(() => {
    const rawQueries = querySelectAll?.data?.data || [];
    if (!currentUserRoleId)
      return rawQueries.some((q: any) => q.querystatus !== "CLOSED");
    return rawQueries.some((q: any) => {
      const details = q.querydetails || [];
      const originatingDetail = details[details.length - 1];
      return (
        q.querystatus !== "CLOSED" &&
        Number(originatingDetail?.byrole) === currentUserRoleId
      );
    });
  }, [querySelectAll?.data, currentUserRoleId]);

  const workflowStages = useMemo(() => {
    const rawStages = workflowQuery?.data?.data?.stages || [];
    // Filter out "Not Started" as requested
    return rawStages.filter((stage: any) => stage.stageName !== "Not Started");
  }, [workflowQuery?.data?.data?.stages]);

  const latestActiveQuery = useMemo(() => {
    const rawQueries = querySelectAll?.data?.data || [];
    const active = rawQueries.find((q: any) => q.querystatus !== "CLOSED");
    if (!active) return null;

    const details = active.querydetails || [];
    const latestDetail = details[details.length - 1];
    const originatingDetail = details[0];

    // Find the stage name from stage history or role
    const raisedFromStage =
      workflowStages.find(
        (s: any) => Number(s.stageId) === Number(originatingDetail?.stageid),
      )?.stageName || "Previous Review";

    return {
      ...active,
      raisedFromStage,
      message: latestDetail?.querymessage || active.querymessage,
      updatedDate: active.updateddate || active.createddate,
    };
  }, [querySelectAll?.data, workflowStages]);

  const hasAllOpenedQueries = useMemo(() => {
    const rawQueries = querySelectAll?.data?.data || [];
    return rawQueries.some((q: any) => q.querystatus === "OPENED");
  }, [querySelectAll?.data]);

  const stageInfo = useMemo(
    () => stageSelectQuery?.data?.data?.[0],
    [stageSelectQuery?.data?.data],
  );

  useEffect(() => {
    if (stageInfo?.islocked) {
      navigate("/master/study");
    }
  }, [stageInfo?.islocked, navigate]);

  const isRoleMatched = useMemo(() => {
    if (!stageInfo || !currentUserRoleId) return false;
    return Number(stageInfo.role) === currentUserRoleId;
  }, [stageInfo, currentUserRoleId]);

  const [codelistsLoaded, setCodelistsLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      await loadCodelistsDetailId(["Checklist_Type_OPTIONS"]);
      setCodelistsLoaded(true);
    };
    load();
  }, []);

  // Logic to determine stage behavior
  const currentStageName = stageInfo?.currentstagename || "";
  const isQA = currentStageName.toLowerCase().includes("qa");
  const isQC = currentStageName.toLowerCase().includes("qc");
  const isChecklistStage = isQA || isQC;

  const displayQuestions = useMemo(() => {
    const rawQuestions = MasterICFQuery?.data?.data ?? [];
    if (!stageInfo) return [];

    // Filter by stage name (QA/QC) logic
    if (!isChecklistStage) return [];

    return rawQuestions.filter((q: any) => {
      // Primary filter: match by currentstageid if the question has a stageid property
      if (q.stageid) {
        return String(q.stageid) === String(stageInfo.currentstageid);
      }

      // Fallback: match by checklist type (QA/QC)
      return q.checklisttypetext === "STUDY";
    });
  }, [MasterICFQuery?.data?.data, isQA, stageInfo]);

  useEffect(() => {
    // Reset/Initialize checks when displayQuestions changes (meaning status switched or data loaded)
    const initialChecks: Record<string, boolean> = {};
    displayQuestions.forEach((q: any) => {
      initialChecks[String(q.masterchecklistid)] = false;
    });
    reset(initialChecks);
  }, [displayQuestions, reset]);

  const handleStageSwitch = (isForward: boolean) => {
    if (!stageInfo) return;

    const payload: any = {
      studyid: numericId,
      workflowid: routeWorkflowId,
      updatedby: userData?.userid,
      currentstageid: stageInfo.currentstageid,
      nextstage: stageInfo.nextstage,
      nextactionlabel: stageInfo.nextactionlabel,
      nextstagestatus: stageInfo.nextstagestatus,
      returnstage: stageInfo.returnstage,
      returnactionlabel: stageInfo.returnactionlabel,
      returnstagestatus: stageInfo.returnstagestatus,
      islocked: stageInfo.islocked,
      remarks: null,
      isForward: isForward,
      checklist: [],
    };

    if (isChecklistStage) {
      payload.checklist = displayQuestions.map((q: any) => ({
        questionid: q.masterchecklistid,
        answerid: isForward ? (getValues(String(q.masterchecklistid)) ? 1 : 0) : 1,
      }));
    }

    switchStageMutation.mutate(payload, {

      onSuccess: (response: any) => {
        const statusCode = response?.statusCode ?? response?.statuscode;
        if (statusCode && statusCode !== 200) {
          showToastMessage(
            "error",
            response?.message || "Failed to transition workflow",
          );
          return;
        }
        showToastMessage("success", response.message);
        queryClient.invalidateQueries({ queryKey: ["workflow-stage-select"] });
      },
      onError: (err: any) => {
        showToastMessage(
          "error",
          err?.message ?? err ?? "Failed to transition workflow",
        );
      },
    });
  };

  const handleApprove = (isForward: boolean) => {
    handleStageSwitch(isForward);
  };

  const handleReject = (isForward: boolean) => {
    handleStageSwitch(isForward);
  };

  // Determine stage progress based on currentstageid from API
  const getProgressValue = () => {
    if (!stageInfo || workflowStages.length === 0) return 0;

    const currentIndex = workflowStages.findIndex(
      (s: any) => s.stageId === stageInfo.currentstageid,
    );

    // If current stage is found in our filtered list, calculate percentage
    if (currentIndex !== -1) {
      if (workflowStages.length === 1) return 100;
      return (100 / (workflowStages.length - 1)) * currentIndex;
    }

    // Fallback: If current stage is "Not Started" (which is filtered out), show 0%
    if (stageInfo.currentstagename === "Not Started") return 0;

    return 0;
  };
  const progressValue = getProgressValue();

  const allChecked =
  !isChecklistStage ||
  displayQuestions.every(
    (q: any) => !q.isrequired || watch(String(q.masterchecklistid))
  );

  // Determine Badge colors dynamically
  const getStatusColor = () => {
    if (stageInfo?.currentstagename === "Approved") return "success";
    if (stageInfo?.currentstagename === "Rejected") return "danger";
    if (isQA) return "info";
    return "warning";
  };
  const badgeColor = getStatusColor();

  const rawQueries = querySelectAll?.data?.data || [];
  const queryFlagColor = rawQueries.some((q: any) => q.querystatus === "OPENED")
    ? "text-danger"
    : rawQueries.some((q: any) => q.querystatus === "RESPONDED")
      ? "text-warning"
      : "text-success";

  if (
    workflowQuery.isFetching ||
    MasterICFQuery.isFetching ||
    stageSelectQuery.isFetching
  )
    return (
      <Container fluid className={`px-0 ${styles.workspaceContainer}`}>
        <FullPageLoader />
      </Container>
    );

  return (
    <Container fluid className={`px-0 ${styles.workspaceContainer}`}>
      {/* 1. TOP NAVIGATION & PROGRESS UI */}
      <div
        className={`bg-white border-bottom px-4 py-3 shadow-sm ${styles.topNav}`}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-3">
            <Button
              color="light"
              size="sm"
              onClick={() => navigate(-1)}
              className={`rounded-circle d-flex justify-content-center align-items-center ${styles.backBtn}`}
            >
              <i className="ri-arrow-left-line fs-18"></i>
            </Button>
            <div>
              <h5 className="mb-1 fw-bold text-dark">
                Review Mode: Protocol Review
              </h5>
              <div className="text-muted small fw-medium">
                <i className="ri-database-2-line me-1"></i> Streamlining Protocol Review Workflow
              </div>
            </div>
          </div>
          <Badge
            color={badgeColor}
            className={`px-4 py-2 fs-13 rounded-pill bg-opacity-10 text-dark border border-${badgeColor}`}
          >
            <span className={`text-${badgeColor} me-2`}>
              <i className="ri-focus-2-line"></i>
            </span>
            {stageInfo?.currentstagestatus || "No"}
          </Badge>
        </div>

        {/* Beautiful Progression Bar */}
        <div className="mt-4 px-5 position-relative mb-3">
          {workflowQuery.isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-2">
              <Spinner size="sm" color="primary" className="me-2" />{" "}
              <span className="text-muted small">
                Loading Workflow Stages...
              </span>
            </div>
          ) : (
            <>
              <Progress
                value={progressValue}
                color="success"
                className={styles.progBar}
              />
              <div
                className={`d-flex justify-content-between position-absolute w-100 px-5 ${styles.progLabelWrapper}`}
              >
                {workflowStages.map((stage: any, index: number) => {
                  const currentStageIndex = workflowStages.findIndex(
                    (s: any) => s.stageId === stageInfo?.currentstageid,
                  );

                  let badgeClass = "bg-light text-muted"; // Upcoming
                  if (index < currentStageIndex) {
                    badgeClass = "bg-success text-white"; // Completed
                  } else if (index === currentStageIndex) {
                    // Current stage (highlight based on QA/QC/Generic)
                    badgeClass = stage.stageName.includes("QA")
                      ? "bg-info text-white"
                      : "bg-success text-white";
                  }

                  return (
                    <span
                      key={stage.stageId}
                      className={`badge ${badgeClass} rounded-pill px-3 py-1 border border-white border-2 shadow-sm fs-12`}
                    >
                      {stage.stageName}
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 2. SPLIT SCREEN WORKSPACE */}
      <div className={`${styles.splitContainer} ${styles.splitWrapper}`}>
        <div className={`${styles.documentPanel} ${styles.docPanelNoPadding}`}>
          <ViewStudyDetails
            studyid={numericId}
            protocolno={null}
            onProtocolChange={() => { }}
            isReviewMode={
              !isRoleMatched || stageInfo.currentstagename !== "Data Entry"
            }
            hideSidebar={true}
          />
        </div>

        {/* Right Side: Dynamic Audit Checklist */}
        <div className={styles.auditPanel}>
          {stageInfo?.islocked ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center">
              <i
                className={`ri-checkbox-circle-fill text-success mb-3 ${styles.statusIconLg}`}
              ></i>
              <h3 className="fw-bold text-dark">Study is Activated!</h3>
              <p className="text-muted px-4">
                All Quality Control (QC) and Quality Assurance (QA) audits have
                been verified and digitally signed. Volunteer enrollment and
                dosing can now begin.
              </p>
              {/* <Button color="primary" className="mt-4 px-4 py-2 rounded-pill fw-bold" onClick={handleRestartDemo}>
                <i className="ri-refresh-line me-1"></i> Restart Demo Flow
              </Button> */}
            </div>
          ) : stageInfo?.currentstagename === "Data Entry" ? (
            <>
              <div className="mb-3 flex-shrink-0">
                <h5 className="fw-bold text-dark d-flex align-items-center justify-content-between">
                  <span>
                    <i className="ri-shield-flash-line me-2 text-primary"></i>
                    {stageInfo?.currentstagename || "Action Required"}
                  </span>
                  <Button
                    color="link"
                    className={`p-0 ${queryFlagColor}`}
                    onClick={toggleQueryModal}
                  >
                    <i className="ri-flag-fill fs-18"></i>
                  </Button>
                </h5>
                <div
                  className="text-muted fw-medium"
                  style={{ fontSize: "13px", marginLeft: "26px" }}
                >
                  Complete the required data entry and forward for review.
                </div>
              </div>

              {/* Query Details Section - Dynamic */}
              {latestActiveQuery ? (
                <div className={styles.queryCard}>
                  <div className={styles.queryHeader}>
                    <div className={styles.queryMeta}>
                      <div className={styles.raisedByName}>
                        <i className="ri-user-received-2-line text-primary fs-18"></i>
                        Raised by:{" "}
                        {latestActiveQuery.querydetails?.at(-1)?.createdbyname || "Reviewer"}
                      </div>
                      <span>
                        <i className="ri-arrow-go-back-line"></i> Returned from:{" "}
                        {/* {latestActiveQuery.querydetails?.at(-1)?.stageid } */}
                        {
                          workflowQuery?.data?.data?.stages?.find(
                            (s: any) =>
                              Number(s.stageId) ===
                              Number(latestActiveQuery.querydetails?.at(-1)?.stageid)
                          )?.stageName || "Unknown Stage"
                        }
                      </span>
                    </div>
                    <div className="text-end">
                      <Badge
                        color={
                          latestActiveQuery.querystatus === "OPENED"
                            ? "danger"
                            : "warning"
                        }
                        className={`bg-opacity-10 ${latestActiveQuery.querystatus === "OPENED" ? "text-danger border-danger" : "text-warning border-warning"} border p-1 px-3 rounded-pill fw-bold fs-11`}
                        style={{ cursor: "pointer" }}
                        onClick={toggleQueryModal}
                      >
                        {latestActiveQuery.querystatus}
                      </Badge>
                      <div
                        className="text-muted mt-2"
                        style={{ fontSize: "11px" }}
                      >
                        <i className="ri-calendar-todo-line me-1"></i>{" "}
                        {formatDateTime(latestActiveQuery.updatedDate)}
                      </div>
                    </div>
                  </div>

                  <div className={styles.queryBody}>
                    <p>{latestActiveQuery.message}</p>
                  </div>
                </div>
              ) : (
                <div className="py-5 text-center bg-light rounded-3 border border-dashed mt-3">
                  <i className="ri-checkbox-circle-line fs-24 text-success mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark mb-1">No Active Queries</h6>
                  <p className="text-muted small mb-0 px-4">
                    No active queries found for this study. You can safely
                    proceed with data entry.
                  </p>
                </div>
              )}

              <div className={`${styles.actionFooter} pt-2 mt-auto`}>
                <div className="d-flex gap-3 w-100">
                  {isRoleMatched && (
                    <>
                      {stageInfo?.returnactionlabel && (
                        <Button
                          color="danger"
                          outline
                          className="flex-fill d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm"
                          disabled={!hasAllOpenedQueries}
                          onClick={() => handleReject(false)}
                        >
                          <i className="ri-close-circle-line fs-18"></i>{" "}
                          {stageInfo?.returnactionlabel}
                        </Button>
                      )}
                      {stageInfo?.nextactionlabel && (
                        <Button
                          color="success"
                          className="flex-fill d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm"
                          disabled={hasAllOpenedQueries}
                          onClick={() => handleApprove(true)}
                        >
                          <i className="ri-check-double-line fs-18"></i>{" "}
                          {stageInfo?.nextactionlabel}
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {isRoleMatched && hasAllOpenedQueries && (
                  <div className="text-danger small mt-2 text-center fw-medium">
                    <i className="ri-alert-line"></i> Resolve all queries to
                    enable the approval option.
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {isChecklistStage && (
                <>
                  <div className="mb-3 flex-shrink-0">
                    <h5 className="fw-bold mb-1 text-dark d-flex align-items-center justify-content-between">
                      <span>
                        <i
                          className={`me-2 ${isQA ? "ri-shield-keyhole-line text-info" : "ri-flask-line text-warning"}`}
                        ></i>
                        {stageInfo?.currentstagename ||
                          (isQA
                            ? "Quality Assurance Audit"
                            : "Quality Control Checklist")}
                      </span>
                      <Button
                        color="link"
                        className={`p-0 ${queryFlagColor}`}
                        onClick={toggleQueryModal}
                      >
                        <i className="ri-flag-fill fs-18"></i>
                      </Button>
                    </h5>
                    {!allChecked && (
                      <div
                        className="text-muted fw-medium"
                        style={{ fontSize: "13px", marginLeft: "26px" }}
                      >
                        Check all boxes to enable final approval sign-off.
                      </div>
                    )}
                  </div>

                  <Card className={styles.checklistCard}>
                    <CardBody className="py-2 px-3">
                      <div className="mb-2">
                        <h6
                          className={`fw-bold mb-0 text-dark border-start border-3 ps-2 py-2 rounded ${isQA ? "border-info bg-info-subtle" : "border-warning bg-warning-subtle"} d-flex align-items-center`}
                        >
                          {isQA
                            ? "Regulatory Verification"
                            : "Core Parameters Verification"}
                        </h6>
                      </div>

                      {/* DYNAMIC CHECKLIST: Rendered based on CommonForm */}
                      <SimpleBar
                        className={styles.checklistBar}
                        style={{
                          maxHeight: "220px",
                          padding: "2px 10px",
                          overflowX: "hidden",
                        }}
                        autoHide={false}
                      >
                        {MasterICFQuery.isLoading ? (
                          <div className="py-4 text-center">
                            <div
                              className="spinner-border spinner-border-sm text-primary me-2"
                              role="status"
                            ></div>
                            <span className="text-muted small">
                              Loading checklist...
                            </span>
                          </div>
                        ) : displayQuestions.length > 0 ? (
                          <CommonForm
                            control={control}
                            columns={1}
                            fields={displayQuestions.map((q: any) => {

                              return {
                                name: String(q.masterchecklistid),
                                label: (
                                  <TruncatedText text={q.question} maxWidth="400px" />
                                ),
                                type: "checkbox",
                                disabled: !isRoleMatched,
                                required: !!q.isrequired,
                              };
                            })}
                          />
                        ) : (
                          <div className="py-4 text-center bg-light rounded shadow-sm border border-dashed">
                            <i className="ri-questionnaire-line fs-24 text-muted mb-2 d-block"></i>
                            <p className="text-muted small mb-0 px-3">
                              No {isQA ? "QA" : "QC"} questions found in the
                              master library.
                            </p>
                          </div>
                        )}
                      </SimpleBar>
                    </CardBody>
                  </Card>
                </>
              )}

              {!isChecklistStage && (
                <div className="mb-3 flex-shrink-0">
                  <h5 className="fw-bold mb-1 text-dark d-flex align-items-center justify-content-between">
                    <span>
                      <i className="ri-shield-flash-line me-2 text-primary"></i>
                      {stageInfo?.currentstagename || "Action Required"}
                    </span>
                    <Button
                      color="link"
                      className={`p-0 ${queryFlagColor}`}
                      onClick={toggleQueryModal}
                    >
                      <i className="ri-flag-fill fs-18"></i>
                    </Button>
                  </h5>
                  <div
                    className="text-muted fw-medium"
                    style={{ fontSize: "13px", marginLeft: "26px" }}
                  >
                    Process this stage by selecting an action below.
                  </div>
                </div>
              )}

              {/* Sticky action footer so it doesn't get lost */}
              <div className={`${styles.actionFooter} pt-2`}>
                <div className="d-flex gap-3 w-100">
                  {isRoleMatched && (
                    <>
                      {stageInfo?.returnactionlabel && (
                        <Button
                          color="danger"
                          outline
                          className="flex-fill d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm"
                          disabled={!hasAllOpenedQueries || switchStageMutation.isPending}
                          onClick={() => handleReject(false)}
                        >
                          <i className="ri-close-circle-line fs-18"></i>{" "}
                          {stageInfo?.returnactionlabel}
                        </Button>
                      )}
                      {stageInfo?.nextactionlabel && (
                        <Button
                          color="success"
                          className="flex-fill d-flex align-items-center justify-content-center gap-2 fw-bold shadow-sm"
                          disabled={hasMyActiveQueries || switchStageMutation.isPending}
                          onClick={() => handleApprove(true)}
                        >
                          <i className="ri-check-double-line fs-18"></i>{" "}
                          {stageInfo?.nextactionlabel}
                        </Button>
                      )}
                    </>
                  )}
                </div>
                {hasMyActiveQueries && (
                  <div className="text-danger small mt-2 text-center fw-medium">
                    <i className="ri-alert-line"></i> Resolve your role's active
                    queries to enable approval.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <QueryDetailsModal
        isOpen={isQueryModalOpen}
        toggle={toggleQueryModal}
        studyid={numericId || undefined}
        stageid={stageInfo?.currentstageid || undefined}
        isRoleMatched={isRoleMatched}
      />
    </Container>
  );
};

export default WorkflowReview;
